/* Task utilities — shared helpers across all task components */

export const STATUS_LABELS = {
  backlog: "Backlog",
  todo: "To Do",
  in_progress: "In Progress",
  waiting: "Waiting",
  done: "Done",
  archived: "Archived",
};

export const STATUS_ORDER = ["backlog", "todo", "in_progress", "waiting", "done"];

export const STATUS_COLORS = {
  backlog: { bg: "rgba(74,74,82,0.08)", c: "#4a4a52" },
  todo: { bg: "rgba(0,94,122,0.1)", c: "#005e7a" },
  in_progress: { bg: "rgba(14,165,233,0.12)", c: "#0284c7" },
  waiting: { bg: "rgba(245,158,11,0.12)", c: "#b45309" },
  done: { bg: "rgba(22,163,74,0.12)", c: "#16a34a" },
  archived: { bg: "rgba(0,0,0,0.05)", c: "#82828c" },
};

export const PRIORITY_LABELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
  // New tier above Urgent. Flame emoji is part of the canonical label
  // so it renders consistently in every place priority is displayed
  // (table, drawer, pickers, search, badges) without needing per-call
  // visual tweaks.
  on_fire: "🔥 On Fire",
};

export const PRIORITY_ORDER = ["low", "medium", "high", "urgent", "on_fire"];

export const PRIORITY_COLORS = {
  low: { bg: "rgba(74,74,82,0.08)", c: "#4a4a52" },
  medium: { bg: "rgba(0,94,122,0.1)", c: "#005e7a" },
  high: { bg: "rgba(245,158,11,0.14)", c: "#b45309" },
  urgent: { bg: "rgba(220,38,38,0.12)", c: "#dc2626" },
  on_fire: { bg: "rgba(234,88,12,0.18)", c: "#c2410c" },
};

// Workspace-aware helpers — every task view (List, Board, Calendar,
// Drawer) receives a `statuses` prop loaded from /api/statuses. These
// helpers merge that workspace list with the static fallbacks so a
// custom status renders as its user-defined name + color everywhere a
// task appears, while a missing/empty `statuses` falls back to the
// static built-in display.
export function statusLabel(statusKey, statuses) {
  if (!statusKey) return STATUS_LABELS.todo;
  if (Array.isArray(statuses) && statuses.length) {
    const found = statuses.find((s) => s.key === statusKey);
    if (found) return found.name;
  }
  return STATUS_LABELS[statusKey] || statusKey;
}

export function statusColor(statusKey, statuses) {
  if (Array.isArray(statuses) && statuses.length) {
    const found = statuses.find((s) => s.key === statusKey);
    if (found) {
      const c = found.color || "#4a4a52";
      // Build a translucent background from the workspace-defined color
      // so custom statuses get the same chip styling as built-ins.
      return { bg: `${c}1f`, c };
    }
  }
  return STATUS_COLORS[statusKey] || STATUS_COLORS.todo;
}

export function statusOrder(statuses) {
  // Prefer the workspace's sort_order. Falls back to the static order
  // when there's nothing loaded yet.
  if (Array.isArray(statuses) && statuses.length) {
    return [...statuses]
      .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99))
      .filter((s) => s.key !== "archived")  // archived gets its own filter
      .map((s) => s.key);
  }
  return STATUS_ORDER;
}

export function formatDueDate(iso, userTz = "UTC") {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const tomorrow = new Date(now.getTime() + 24 * 3600 * 1000);
    const yesterday = new Date(now.getTime() - 24 * 3600 * 1000);
    if (sameDay) return "Today";
    if (d.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return new Intl.DateTimeFormat(undefined, {
      timeZone: userTz,
      month: "short",
      day: "numeric",
      year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    }).format(d);
  } catch {
    return iso.slice(0, 10);
  }
}

export function isOverdue(iso) {
  if (!iso) return false;
  // Compare due date and "now" in the user's local timezone (not UTC) so a task due
  // "today" doesn't get flagged overdue near day boundaries due to UTC offset.
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return false;
  const now = new Date();
  // Start of the user's local "today"
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return due < startOfToday;
}

export function initials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
